import type { Static } from "@sinclair/typebox";
import { type AdminUser, ROLES, type Role } from "@volgo/core/contract";
import { useState } from "react";
import { useSession } from "../../session";
import { Button } from "../../ui/Button";
import { describeError } from "../../ui/errors";
import { formatDateTime } from "../../ui/format";
import { ROLE_LABELS } from "../../ui/labels";
import { PagedFooter } from "../../ui/PagedFooter";
import { usePaged } from "../../ui/useLoad";

type User = Static<typeof AdminUser>;

export function UsersPage() {
  const { client } = useSession();
  const [error, setError] = useState<unknown>(null);
  const list = usePaged<User>(
    (cursor) => client.call("admin.listUsers", { query: { limit: 50, ...(cursor ? { cursor } : {}) } }),
    "users",
  );

  function patch(user: User, body: { role?: Role; disabled?: boolean }) {
    setError(null);
    client.call("admin.patchUser", { params: { id: user.id }, body }).then(
      (next) => list.setItems((items) => items.map((u) => (u.id === next.id ? next : u))),
      (e: unknown) => setError(e),
    );
  }

  return (
    <section aria-label="사용자">
      {error !== null && (
        <p className="note note-warn" role="alert">
          {describeError(error, {
            CONFLICT:
              "마지막 관리자는 권한을 내리거나 정지할 수 없습니다. 먼저 다른 계정을 관리자로 올리세요.",
          })}
        </p>
      )}
      {list.items.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">이름</th>
                <th scope="col">권한</th>
                <th scope="col">로그인 방법</th>
                <th scope="col">마지막 로그인</th>
                <th scope="col">상태</th>
              </tr>
            </thead>
            <tbody>
              {list.items.map((user) => (
                <tr key={user.id}>
                  <th scope="row">{user.username}</th>
                  <td>
                    <select
                      className="select"
                      aria-label={`${user.username} 권한`}
                      value={user.role}
                      onChange={(e) => patch(user, { role: e.target.value as Role })}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABELS[r]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{user.discordId ? "Discord" : "접근 키"}</td>
                  <td>{user.lastLoginAt ? formatDateTime(user.lastLoginAt) : "—"}</td>
                  <td>
                    {user.disabledAt ? (
                      <>
                        <span className="note-warn">정지됨</span>{" "}
                        <Button
                          aria-label={`${user.username} 정지 풀기`}
                          onClick={() => patch(user, { disabled: false })}
                        >
                          정지 풀기
                        </Button>
                      </>
                    ) : (
                      <Button
                        aria-label={`${user.username} 계정 정지`}
                        onClick={() => patch(user, { disabled: true })}
                      >
                        계정 정지
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <PagedFooter list={list} emptyTitle="계정이 없습니다" />
    </section>
  );
}
